import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CORE_PILLARS, SITE_INFO } from '../data/siteData';
import { fetchServices, fetchForside, FALLBACK_FORSIDE } from '../lib/cms';
import ReviewsSlider from '../components/ReviewsSlider';
import InitialConsultationCallout from '../components/InitialConsultationCallout';
import ContactForm from '../components/ContactForm';

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [forsideData, setForsideData] = useState(FALLBACK_FORSIDE);
  const [showAllPillars, setShowAllPillars] = useState(false);

  useEffect(() => {
    async function loadData() {
      const [sData, fData] = await Promise.all([
        fetchServices(),
        fetchForside(),
      ]);
      if (sData) setServices(sData);
      if (fData) setForsideData(fData);
    }
    loadData();
  }, []);

  // Parse pillars with dedicated individual CMS fields so customer edits never break the card layout
  const pillars = useMemo(() => {
    const list = [];
    for (let i = 1; i <= 6; i++) {
      const title = forsideData?.[`tilgang_kasse_${i}_titel`];
      const desc = forsideData?.[`tilgang_kasse_${i}_tekst`];
      const fallback = CORE_PILLARS[i - 1];

      if (title || desc || fallback) {
        list.push({
          id: `pillar-${i}`,
          title: title ?? fallback?.title ?? '',
          description: desc ?? fallback?.description ?? '',
          icon: fallback?.icon || 'fa-solid fa-seedling',
        });
      }
    }
    if (list.length > 0) return list;

    return CORE_PILLARS;
  }, [forsideData]);

  // Parse qualifications for Om Joy section
  const kvalifikationer = useMemo(() => {
    const raw = forsideData?.om_joy_kvalifikationer || "Socialpædagog & Autismespektrum | Neuroaffektiv Psykoterapeut (stud.)";
    return raw.split('|').map((s) => s.trim()).filter(Boolean);
  }, [forsideData?.om_joy_kvalifikationer]);

  // Image URLs with Cloudflare transforms
  const heroBg = forsideData?.hero_baggrundsbillede?.[0] || FALLBACK_FORSIDE.hero_baggrundsbillede[0];
  const heroBgOptimized = heroBg.startsWith('http') && !heroBg.includes('/cdn-cgi/image/')
    ? heroBg.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/')
    : heroBg;

  const omJoyImg = forsideData?.om_joy_billede?.[0] || FALLBACK_FORSIDE.om_joy_billede[0];
  const omJoyImgOptimized = omJoyImg.startsWith('http') && !omJoyImg.includes('/cdn-cgi/image/')
    ? omJoyImg.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/')
    : omJoyImg;

  const heroPhone = forsideData?.hero_telefon || SITE_INFO.phone;
  const heroPhoneLink = heroPhone.replace(/\s+/g, '');

  return (
    <div className="bg-[#FAF7F2]">
      <SEOHead
        title="KerneViden"
        description="Mennekser regulerer sig forskelligt. Derfor starter jeg der, hvor du er. Det er ikke vigtigt for mig hvordan du er, men hvordan du har det."
        ogImage="https://assets.auxodev.dk/assets/1d30bca3-a748-4ede-8335-828014d68a29/fullsizerender-4_aa527c92.jpeg"
      />

      {/* Hero Section - Left aligned content with Webform on the right */}
      <section className="relative pt-12 pb-24 sm:pt-16 sm:pb-32 lg:pt-16 lg:pb-36 overflow-hidden min-h-[580px] flex items-center">
        {/* Background Nature Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroBgOptimized}
            alt="KerneViden rolig og lys natur"
            className="w-full h-full object-cover object-center"
          />
          {/* Calming soft overlay transitioning to clean white at bottom */}
          <div className="absolute inset-0 bg-[#FAF7F2]/45 sm:bg-[#FAF7F2]/40" />
          <div className="absolute inset-x-0 bottom-0 h-28 sm:h-36 bg-gradient-to-t from-white via-white/70 to-transparent" />
        </div>

        {/* Hero Content - Grid with text & CTA on left, Webform on right */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Heading, description, and CTA under description */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-7 text-left">
              {/* Main Headline - Left aligned */}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#233424] font-normal leading-[1.15] tracking-tight italic max-w-2xl text-left">
                {forsideData.hero_overskrift || FALLBACK_FORSIDE.hero_overskrift}
              </h1>

              {/* Supporting Intro Text */}
              <p className="text-base sm:text-lg text-[#4A5749] leading-relaxed font-normal max-w-xl text-left">
                {forsideData.hero_undertekst || FALLBACK_FORSIDE.hero_undertekst}
              </p>

              {/* CTA Underneath Description */}
              <div className="pt-1 flex flex-wrap items-center gap-5 sm:gap-7">
                <a
                  href={`tel:${heroPhoneLink}`}
                  className="inline-flex items-center justify-start py-2 text-base sm:text-lg font-medium text-[#233424] hover:text-[#3A4F3D] transition-all duration-200 cursor-pointer whitespace-nowrap group hover:scale-105 active:scale-95"
                >
                  <i className="fa-solid fa-phone mr-2.5 text-sm text-[#233424] group-hover:text-[#3A4F3D] transition-colors" aria-hidden="true" />
                  <span>{heroPhone}</span>
                </a>

                <Link
                  to="/vores-tjenester"
                  className="inline-flex items-center justify-start py-2 text-base sm:text-lg font-medium text-[#233424] hover:text-[#3A4F3D] transition-all duration-200 cursor-pointer whitespace-nowrap group hover:scale-105 active:scale-95"
                >
                  <span>{forsideData.hero_knap_tekst || FALLBACK_FORSIDE.hero_knap_tekst}</span>
                  <i className="fa-solid fa-arrow-right ml-2 text-xs text-[#233424] group-hover:text-[#3A4F3D] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Right Column: Webform in Hero */}
            <div className="lg:col-span-5 w-full">
              <ContactForm
                title={forsideData.hero_formular_titel || "Kontakt mig"}
                subtitle={forsideData.hero_formular_undertekst || "Få en uforpligtende samtale eller hør mere."}
                compact={true}
              />
            </div>

          </div>
        </div>
      </section>

      {/* The Core Pillars / Tilgang from CMS */}
      <section className="py-12 sm:py-16 bg-white border-b border-[#EFEAE2]" aria-label="Kerneværdier og tilgang">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-8 sm:mb-10 text-left">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#233424] font-normal tracking-tight">
              {forsideData.tilgang_overskrift || "Hvordan jeg møder dig"}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[#566555]">
              {forsideData.tilgang_undertekst || "Det er ikke vigtigt for mig hvordan du er, men hvordan du har det."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {(showAllPillars ? pillars : pillars.slice(0, 3)).map((pillar) => (
              <div
                key={pillar.id}
                className="bg-white rounded-3xl p-6 sm:p-8 pb-5 sm:pb-6 shadow-[0_18px_48px_rgba(44,62,45,0.10)] hover:shadow-[0_26px_65px_rgba(44,62,45,0.16)] flex flex-col justify-between items-center text-center transition-all duration-300 hover:-translate-y-1.5 group"
              >
                <div className="flex flex-col items-center">
                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-[#233424] tracking-tight mb-2.5">
                    {pillar.title}
                  </h3>
                  <p className="text-sm sm:text-base leading-relaxed text-[#4A5749] font-normal">
                    {pillar.description}
                  </p>
                </div>

                <div className="pt-4 sm:pt-5 w-full flex justify-center">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center text-xs font-semibold text-[#3A4F3D] hover:text-black transition-colors group-hover:text-black"
                  >
                    <span>Læs mere om forløb</span>
                    <i className="fa-solid fa-arrow-right text-[10px] ml-1.5 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {pillars.length > 3 && (
            <div className="mt-8 sm:mt-10 text-center">
              <button
                type="button"
                onClick={() => setShowAllPillars(!showAllPillars)}
                className="inline-flex items-center justify-center text-sm sm:text-base font-medium text-[#233424] hover:text-black transition-colors cursor-pointer group py-2"
              >
                <span className="underline underline-offset-4 decoration-[#233424]/30 group-hover:decoration-black">
                  {showAllPillars ? 'Vis færre' : (forsideData.tilgang_knap_tekst || 'Se mere om tilgangen')}
                </span>
                <i
                  className={`fa-solid fa-chevron-down ml-2 text-xs transition-transform duration-200 ${
                    showAllPillars ? 'rotate-180 text-black' : 'text-[#7D8A79] group-hover:text-black'
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Om mig / Mød Joy Section */}
      <section className="py-16 sm:py-20 bg-[#FAF7F2]" aria-label="Om Joy og KerneViden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            
            {/* Image Column */}
            <div className="lg:col-span-6 flex justify-center lg:justify-start">
              <div className="w-full max-w-lg lg:max-w-none">
                <img
                  src={omJoyImgOptimized}
                  alt="Joy - KerneViden"
                  className="w-full h-auto max-h-[600px] object-cover object-center rounded-2xl"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Content Column */}
            <div className="lg:col-span-6 space-y-6">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl text-[#233424] font-normal tracking-tight">
                  {forsideData.om_joy_overskrift || "Et trygt og nærværende rum"}
                </h2>
              </div>

              <div className="text-sm sm:text-base text-[#4A5749] leading-relaxed">
                {forsideData.om_joy_tekst?.includes('<p') ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: forsideData.om_joy_tekst }}
                    className="space-y-4"
                  />
                ) : (
                  <p>
                    {forsideData.om_joy_tekst || "Mit navn er Joy. Jeg skaber et trygt og nærværende rum, hvor du bliver mødt præcis som du er — med udgangspunkt i dit tempo, dit nervesystem og dine egne ressourcer."}
                  </p>
                )}
              </div>

              {/* Quick highlights / qualifications */}
              <div className="flex flex-col gap-2.5 pt-1">
                {kvalifikationer.map((kv, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-sm sm:text-base text-[#2C3E2D] font-medium">
                    <i
                      className={`${idx === 0 ? 'fa-solid fa-graduation-cap' : 'fa-solid fa-heart-pulse'} text-black text-sm shrink-0`}
                      aria-hidden="true"
                    />
                    <span>{kv}</span>
                  </div>
                ))}
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-os"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium text-white bg-[#3A4F3D] hover:bg-[#2C3E2D] shadow-xs hover:shadow transition-all duration-200 group cursor-pointer hover:scale-105 active:scale-95"
                >
                  <span>{forsideData.om_joy_knap_tekst || "Læs hele min historie"}</span>
                  <i className="fa-solid fa-arrow-right ml-2 text-xs text-[#E3C2B0] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>

                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-medium text-[#233424] bg-transparent hover:bg-transparent border border-[#233424]/40 hover:border-[#233424] transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95"
                >
                  <span>Book en samtale</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Forløb & Ydelser section */}
      <section className="py-16 sm:py-20 bg-white border-t border-[#EFEAE2]" aria-label="Forløb og tilbud">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#233424] font-normal tracking-tight">
                {forsideData.forloeb_overskrift || "Forløb tilpasset dig og dine behov."}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-[#566555] max-w-xl">
                {forsideData.forloeb_undertekst || "Få mere at vide om, hvilket forløb der passer til dig."}
              </p>
            </div>

            <Link
              to="/vores-tjenester"
              className="inline-flex items-center text-sm font-semibold text-[#3A4F3D] hover:text-[#C67D5A] transition-colors self-start md:self-auto"
            >
              <span>{forsideData.forloeb_link_tekst || "Se alle forløb og priser"}</span>
              <i className="fa-solid fa-arrow-right text-xs ml-2" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {services.map((service) => {
              const plainSummary = (service.description || service.beskrivelse)
                ? (service.description || service.beskrivelse).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
                : '';

              return (
                <div
                  key={service.id || service.custom_slug || service.brugerdefineret_slug}
                  className="bg-white rounded-2xl p-6 sm:p-7 shadow-[0_16px_45px_rgba(44,62,45,0.08)] hover:shadow-[0_24px_60px_rgba(44,62,45,0.14)] flex flex-col justify-between transition-all duration-300"
                >
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl font-medium text-[#233424] tracking-tight">
                        {service.title || service.titel}
                      </h3>
                    </div>

                    <p className="text-xs sm:text-sm text-[#4E5C4E] leading-relaxed line-clamp-2">
                      {plainSummary}
                    </p>
                  </div>

                  <div className="pt-4 mt-5 border-t border-[#EAE2D7] flex items-center justify-between gap-3">
                    <Link
                      to="/vores-tjenester"
                      className="inline-flex items-center text-sm font-semibold text-[#3A4F3D] hover:text-[#C67D5A] transition-colors"
                    >
                      <span>Læs hele forløbet</span>
                      <i className="fa-solid fa-arrow-right text-xs ml-1.5" aria-hidden="true" />
                    </Link>

                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm font-semibold text-[#233424] bg-transparent hover:bg-transparent border border-[#233424]/40 hover:border-[#233424] transition-all duration-200 cursor-pointer hover:scale-105 active:scale-95 whitespace-nowrap"
                    >
                      Book samtale
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Central USP: Gratis Indledende Samtale Callout */}
      <InitialConsultationCallout
        bgImage={forsideData.gratis_samtale_baggrundsbillede?.[0]}
        title={forsideData.gratis_samtale_overskrift}
        description={forsideData.gratis_samtale_beskrivelse}
        ctaText={forsideData.gratis_samtale_knap_tekst}
        phone={forsideData.gratis_samtale_telefon}
      />

      {/* Klientudtalelser / Reviews Slider */}
      <ReviewsSlider
        title={forsideData.udtalelser_overskrift}
        subtitle={forsideData.udtalelser_undertekst}
      />
    </div>
  );
}
